import { Box, Eye, Shapes } from "lucide-react";
import type { FC } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/primitives/Dropdown";
import { useViewer, type ViewerType } from "@/context/ViewerContext";
import { transitions } from "@/styles/transitions";
import { cn } from "@/utils/cn";
import { themeComponentStyles } from "@/utils/themeUtils";

const VIEWER_OPTIONS: {
  id: ViewerType;
  label: string;
  icon: FC<{ className?: string }>;
}[] = [
  { icon: Box, id: "babylon", label: "BabylonJS" },
  { icon: Shapes, id: "three.js", label: "Three.js" },
  { icon: Eye, id: "model-viewer", label: "Model Viewer" },
];

/**
 * Dropdown for switching between 3D viewer engines.
 * Visible on /demo/3d/* routes.
 */
export const ViewerSelect: FC = () => {
  const { viewerType, setViewerType } = useViewer();

  const current =
    VIEWER_OPTIONS.find((o) => o.id === viewerType) ?? VIEWER_OPTIONS[0];
  const CurrentIcon = current.icon;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Select 3D viewer"
        className={cn(
          "flex items-center gap-2 text-sm px-2.5 cursor-pointer flex-shrink-0",
          transitions.colors,
          themeComponentStyles.getContainerClasses(),
          "hover:bg-gray-100 dark:hover:bg-gray-700/50",
          themeComponentStyles.getControlClasses(),
        )}
      >
        <CurrentIcon className="h-4 w-4" />
        <svg
          aria-hidden="true"
          className="h-4 w-4 transition-transform-standard hidden sm:block"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <title>Toggle dropdown</title>
          <path
            d="M19 9l-7 7-7-7"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
          />
        </svg>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[12rem] p-1"
        sideOffset={4}
      >
        <DropdownMenuLabel>3D Viewer</DropdownMenuLabel>

        {VIEWER_OPTIONS.map((option) => {
          const isActive = viewerType === option.id;
          const Icon = option.icon;

          return (
            <DropdownMenuItem
              className={cn(
                "relative flex w-full cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none",
                transitions.colors,
                isActive
                  ? "bg-gray-100 dark:bg-gray-700/50 text-theme-primary dark:text-theme-primary"
                  : "hover:bg-gray-100/70 dark:hover:bg-gray-700/40 text-gray-700 dark:text-gray-200",
              )}
              key={option.id}
              onSelect={() => setViewerType(option.id)}
            >
              <Icon className="h-4 w-4" />
              <span className="font-medium">{option.label}</span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
